<template>
  <section class="relative bg-brand-dark min-h-screen flex flex-col items-center justify-center px-6 md:px-12 text-brand-cream paper-texture overflow-hidden pt-24 pb-16">
    <SketchDoodle type="star" :size="16" top="10%" left="5%" motion="float" :opacity="0.45" hide-on-mobile />
    <SketchDoodle type="dots" :size="32" top="14%" right="8%" :opacity="0.35" hide-on-mobile />
    <SketchDoodle type="squiggle" :size="36" bottom="10%" right="10%" :opacity="0.3" hide-on-mobile />

    <div class="min-h-[calc(100vh-6rem)] flex flex-col items-start justify-center w-full max-w-5xl mx-auto relative z-10">
      <p class="sketch-dev text-sm mb-2" aria-hidden="true">// path so far</p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 w-full">
        <!-- Education Section -->
        <div>
          <div class="relative mb-2">
            <h2 class="text-3xl md:text-4xl font-extrabold text-brand-cream tracking-tight">
              <span class="sketch-underline">Education</span>
            </h2>
            <span class="sketch-note absolute -top-4 right-0 rotate-6 hidden sm:block" aria-hidden="true">learning ∞</span>
          </div>
          <SketchDivider />

          <div class="mb-10">
            <h3 class="text-xl md:text-2xl font-bold mb-2">Bachelor's Degree in Software Engineering</h3>
            <div class="flex flex-wrap items-center mb-2 gap-x-4">
              <span class="text-sm text-brand-muted">BahirDar University</span>
              <span class="text-sm text-brand-muted">Graduation: 2026</span>
            </div>
            <ul class="list-disc list-inside text-sm text-brand-muted mb-2 ml-2">
              <li><span class="font-semibold text-brand-cream">Major:</span> Software Engineering</li>
              <li><span class="font-semibold text-brand-cream">GPA:</span> 3.9</li>
            </ul>
          </div>
          <div class="mb-10">
            <h3 class="text-xl md:text-2xl font-bold mb-2">Bachelor's Degree in Management</h3>
            <div class="flex flex-wrap items-center mb-2 gap-x-4">
              <span class="text-sm text-brand-muted">St. Mary University</span>
              <span class="text-sm text-brand-muted">Graduation: 2026</span>
            </div>
            <ul class="list-disc list-inside text-sm text-brand-muted mb-2 ml-2">
              <li><span class="font-semibold text-brand-cream">Major:</span> Management</li>
              <li><span class="font-semibold text-brand-cream">GPA:</span> 3.2</li>
            </ul>
          </div>
          <div class="mb-10">
            <h3 class="text-xl md:text-2xl font-bold mb-2">A2SV Remote Education</h3>
            <div class="flex items-center mb-2">
              <span class="text-sm text-brand-muted mr-4">Graduation: 2025</span>
            </div>
            <ul class="list-disc list-inside text-sm text-brand-muted mb-2 ml-2">
              <li><span class="font-semibold text-brand-cream">Major:</span> Data structures and algorithms</li>
              <li><span class="font-semibold text-brand-cream">Activities:</span> Solved 500+ leetcode questions</li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl md:text-2xl font-bold mb-2">10 Academy: Kifya AI Mastery program</h3>
            <div class="flex items-center mb-2">
              <span class="text-sm text-brand-muted mr-4">Graduation: 2025</span>
            </div>
            <ul class="list-disc list-inside text-sm text-brand-muted mb-2 ml-2">
              <li><span class="font-semibold text-brand-cream">Major:</span> Artificial Intelligence and Machine learning</li>
              <li><span class="font-semibold text-brand-cream">Activities:</span> Worked on 10+ real life projects</li>
            </ul>
          </div>
        </div>

        <!-- Experience Section -->
        <div>
          <div class="relative mb-2">
            <h2 class="text-3xl md:text-4xl font-extrabold text-brand-cream tracking-tight">
              <span class="sketch-underline">Experience</span>
            </h2>
            <span class="sketch-label sticker absolute -top-3 right-0 border border-brand-muted/40 px-2 py-0.5 bg-brand-surface/50 hidden sm:inline" aria-hidden="true">
              currently building
            </span>
          </div>
          <SketchDivider />

          <!-- A2SV -->
          <div class="mb-10">
            <div class="flex flex-wrap items-center mb-2 gap-2">
              <img src="https://a2sv.org/favicon.ico" alt="A2SV Logo" class="w-8 h-8 rounded mr-1" />
              <span class="text-lg md:text-xl font-bold text-brand-cream">A2SV</span>
              <span class="text-sm text-brand-muted">March, 2025 - Present</span>
            </div>
            <div class="ml-3 sm:ml-11 border-l border-dashed border-brand-muted/50 pl-6">
              <div class="mb-8">
                <h3 class="text-lg md:text-xl font-semibold text-brand-cream mb-1">Backend Intern – AfroVintage Project</h3>
                <p class="text-sm text-brand-muted mb-2">
                  Backend engineer intern for a scalable secondhand clothing marketplace. Contributed to supplier/reseller workflows, REST API development, MongoDB modeling, Dockerization, and deployment on Render.
                </p>
                <ul class="list-disc list-inside text-sm text-brand-muted mb-2">
                  <li><span class="font-semibold text-brand-cream">Technologies:</span> Golang, Gin, MongoDB, Docker, Render</li>
                  <li><span class="font-semibold text-brand-cream">Impact:</span> Built scalable backend and improved deployment workflow.</li>
                </ul>
              </div>
              <div>
                <h3 class="text-lg md:text-xl font-semibold text-brand-cream mb-1">Junior Backend Developer – AfroChat AI Project</h3>
                <p class="text-sm text-brand-muted mb-2">
                  Backend team member for an AI-powered chat application, focusing on backend architecture, data flow, and system scalability.
                </p>
                <ul class="list-disc list-inside text-sm text-brand-muted mb-2">
                  <li><span class="font-semibold text-brand-cream">Technologies:</span> Python, Litstar, PostgreSQL</li>
                  <li><span class="font-semibold text-brand-cream">Impact:</span> Contributed to scalable, AI-driven chat backend.</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Techsisters -->
          <div class="mb-10">
            <div class="flex flex-wrap items-center mb-2 gap-2">
              <img src="https://avatars.githubusercontent.com/u/168320865?s=200&v=4" alt="Techsisters Logo" class="w-8 h-8 rounded mr-1" />
              <span class="text-lg md:text-xl font-bold text-brand-cream">Techsisters</span>
              <span class="text-sm text-brand-muted">August, 2024 - Present</span>
            </div>
            <div class="ml-3 sm:ml-11 border-l border-dashed border-brand-muted/50 pl-6">
              <h3 class="text-lg md:text-xl font-semibold text-brand-cream mb-1">Cofounder</h3>
              <p class="text-sm text-brand-muted mb-2">
                Designed and developed <span class="font-semibold text-brand-cream">Tenagari Andebet</span>, a secure and user-friendly mobile app for reporting and accessing resources to support victims of sexual harassment in Ethiopia.
              </p>
              <ul class="list-disc list-inside text-sm text-brand-muted mb-2">
                <li><span class="font-semibold text-brand-cream">Technologies:</span> Flutter, Dart</li>
                <li><span class="font-semibold text-brand-cream">Impact:</span> Automated repetitive tasks, improved efficiency, and empowered users to access support resources securely.</li>
              </ul>
            </div>
          </div>

          <!-- Oasis InfoByte -->
          <div>
            <div class="flex flex-wrap items-center mb-2 gap-2">
              <img src="https://media.licdn.com/dms/image/C560BAQFQwKkQvQwQ1g/company-logo_200_200/0/1630642469432?e=1723680000&v=beta&t=QkQwQwQwQwQwQwQwQwQwQwQwQwQwQwQwQwQwQwQwQw" alt="Oasis InfoByte Logo" class="w-8 h-8 rounded mr-1" />
              <span class="text-lg md:text-xl font-bold text-brand-cream">Oasis InfoByte</span>
              <span class="text-sm text-brand-muted">October, 2024 - November, 2024</span>
            </div>
            <div class="ml-3 sm:ml-11 border-l border-dashed border-brand-muted/50 pl-6">
              <h3 class="text-lg md:text-xl font-semibold text-brand-cream mb-1">Python Programmer</h3>
              <p class="text-sm text-brand-muted mb-2">
                Built three mini-projects to demonstrate core Python skills:
              </p>
              <ul class="list-disc list-inside text-sm text-brand-muted mb-2">
                <li>
                  <span class="font-semibold text-brand-cream">Password Generator:</span>
                  Created a secure password generator with customizable complexity and length.
                </li>
                <li>
                  <span class="font-semibold text-brand-cream">BMI Calculator:</span>
                  Developed a user-interactive app to calculate and interpret Body Mass Index.
                </li>
                <li>
                  <span class="font-semibold text-brand-cream">Voice Assistant:</span>
                  Implemented a simple voice-controlled assistant using Python libraries like <span class="font-mono">speech_recognition</span> and <span class="font-mono">pyttsx3</span>.
                </li>
              </ul>
              <p class="text-sm text-brand-muted mb-2">
                Strengthened understanding of control structures, functions, file handling, and basic automation in Python.
                Gained experience in writing clean, functional code and working independently on project-based learning.
              </p>
              <ul class="list-disc list-inside text-sm text-brand-muted mb-2">
                <li><span class="font-semibold text-brand-cream">Technologies:</span> Python, speech_recognition, pyttsx3</li>
                <li><span class="font-semibold text-brand-cream">Impact:</span> Demonstrated practical Python skills and independent project work.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SketchDoodle from "./SketchDoodle.vue";
import SketchDivider from "./SketchDivider.vue";

export default {
  name: 'EducationExperience',
  components: { SketchDoodle, SketchDivider },
};
</script>
